/**
 * Ads KPIs par plateforme — la rangée « Ads KPIs » de Looker Studio, détaillée.
 *
 * Une ligne par plateforme cochée, puis le total (toujours affiché). CTR et CVR du total sont
 * recalculés sur les sommes (clics totaux ÷ impressions totales), jamais
 * moyennés : une moyenne de taux donnerait le même poids à 100 et à 100 000
 * impressions.
 */

import { adsRoas } from '@shared/types/sales';
import { formatDecimal, formatInteger, formatMoney, formatPercent } from '../lib/format';
import { Card } from './Card';

export interface AdsKpiRow {
  /** Plateforme : porte sa couleur. */
  key: 'meta' | 'amazon' | 'google';
  platform: string;
  cost: number;
  impressions: number;
  clicks: number;
  conversions: number;
  conversionValue: number;
}

const rate = (numerator: number, denominator: number) =>
  denominator > 0 ? formatPercent((numerator / denominator) * 100) : '—';

/** ROAS : « ×1,53 ». */
const ratioFormatter = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function AdsKpiTable({ rows, currency }: { rows: readonly AdsKpiRow[]; currency: string }) {
  const total = rows.reduce<Omit<AdsKpiRow, 'key'>>(
    (acc, row) => ({
      platform: 'Total',
      cost: acc.cost + row.cost,
      impressions: acc.impressions + row.impressions,
      clicks: acc.clicks + row.clicks,
      conversions: acc.conversions + row.conversions,
      conversionValue: acc.conversionValue + row.conversionValue,
    }),
    { platform: 'Total', cost: 0, impressions: 0, clicks: 0, conversions: 0, conversionValue: 0 },
  );
  const lines: ReadonlyArray<Omit<AdsKpiRow, 'key'>> = rows.length > 0 ? [...rows, total] : rows;

  return (
    <Card title="Performances by platform" subtitle="Plateformes cochées sur la période, puis leur total" className="chart-grid__wide">
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Platform</th>
              <th className="num">Cost HT</th>
              <th className="num">Impressions</th>
              <th className="num">Clicks</th>
              <th className="num">CTR %</th>
              <th className="num">Conversions</th>
              <th className="num">CVR %</th>
              <th className="num">Conversion Value TTC</th>
              <th className="num">ROAS HT</th>
              <th className="num">CPC HT</th>
              <th className="num">CPM HT</th>
              <th className="num">CPA HT</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((row) => {
              const strong = row.platform === 'Total';
              const cell = (content: string) => <td className="num">{strong ? <strong>{content}</strong> : content}</td>;
              return (
                <tr key={row.platform}>
                  <td>{strong ? <strong>{row.platform}</strong> : row.platform}</td>
                  {cell(formatMoney(row.cost, currency))}
                  {cell(formatInteger(row.impressions))}
                  {cell(formatInteger(row.clicks))}
                  {cell(rate(row.clicks, row.impressions))}
                  {cell(formatDecimal(row.conversions))}
                  {cell(rate(row.conversions, row.clicks))}
                  {cell(formatMoney(row.conversionValue, currency))}
                  {cell(adsRoas(row.conversionValue, row.cost) !== null ? `×${ratioFormatter.format(adsRoas(row.conversionValue, row.cost) ?? 0)}` : '—')}
                  {cell(row.clicks > 0 ? formatMoney(row.cost / row.clicks, currency, true) : '—')}
                  {cell(row.impressions > 0 ? formatMoney((row.cost / row.impressions) * 1000, currency, true) : '—')}
                  {cell(row.conversions > 0 ? formatMoney(row.cost / row.conversions, currency, true) : '—')}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
